import { useMemo, type MouseEvent, type Ref } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Bell,
  MessageSquare,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Search,
  SlidersHorizontal,
  SquarePen,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigationStack } from "@/contexts/NavigationStackContext";
import { requestChat, useChatThreads, type ChatRequest } from "@/features/chat/chatThreads";
import { AddContentFlowMenu } from "@/components/navigation/AddContentFlowMenu";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import type { useAddContentFlow } from "@/hooks/useAddContentFlow";
import { useProfile } from "@/hooks/useProfile";
import { useTabNavigation } from "@/hooks/useTabNavigation";
import { useUnreadCount } from "@/hooks/useUnreadCount";
import { getTabIdFromPath } from "@/routes/routeObjects";
import { CHAT, HOME_LAYOUT, NOTIFICATIONS, PROFILE } from "@/routes";
import { analytics } from "@/services/analytics.service";
import { useMenu } from "./MenuContext";
import { HOME_INFO, PAGE_INFO, type PageInfo } from "./pages";
import { HOME_PAGE } from "./homeLayout";
import { useHomeLayout } from "./useHomeLayout";
import { CAPTION, CONTROL, LABEL, ROW_TITLE } from "@/lib/typography";
import { cn } from "@/lib/utils";

type AddContentFlow = ReturnType<typeof useAddContentFlow>;

interface NavigationMenuProps {
  flow: AddContentFlow;
  close: () => void;
  headingRef?: Ref<HTMLHeadingElement>;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

/**
 * The menu's contents, after the native NavigationMenuView: Stasht and the
 * notifications bell; the menu's pages in the person's order (Home among them); recent
 * chats; Add and New Chat; Profile & settings and Search.
 */
export function NavigationMenu({ flow, close, headingRef, collapsed, onToggleCollapsed }: NavigationMenuProps) {
  const docked = useMenu()?.docked ?? false;
  const { layout } = useHomeLayout();
  const { handleTabClick } = useTabNavigation();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeTab, stack } = useNavigationStack();
  const currentTab =
    getTabIdFromPath(location.pathname) ?? (stack.length > 0 && stack[0].underIdx !== null ? activeTab : null);
  const unread = useUnreadCount() > 0;
  const { data: profile } = useProfile();
  const name = profile?.name || profile?.email?.split("@")[0] || "Your profile";

  // Tab pages keep their retained place (and new-tab clicks) through the tab handler.
  const choose = (page: PageInfo) => (event: MouseEvent<HTMLAnchorElement>) => {
    if (page.tab) handleTabClick(page.tab, event);
    if (!event.metaKey && !event.ctrlKey) close();
  };

  const pageRow = (page: PageInfo) => {
    const Icon = page.icon;
    const active = page.tab ? page.tab === currentTab : location.pathname === page.to;
    return (
      <li key={page.to}>
        <Link
          to={page.to}
          onClick={choose(page)}
          aria-current={active ? "page" : undefined}
          className="navigation-link"
          aria-label={page.title}
          title={collapsed ? page.title : undefined}
        >
          <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
          <span className="navigation-label">{page.title}</span>
        </Link>
      </li>
    );
  };

  const openChat = (request: ChatRequest) => {
    close();
    requestChat(request);
    navigate(CHAT());
  };

  const toggleAdd = () => {
    if (!flow.isMenuOpen) {
      analytics.capture("mobile_add_tapped", { location: "menu", current_tab: currentTab ?? "unknown" });
    }
    flow.toggleMenu();
  };

  const notifications = (
    <Button
      type="button"
      variant={docked ? "ghost" : "secondary"}
      size="icon"
      onClick={() => {
        close();
        navigate(NOTIFICATIONS());
      }}
      aria-label="Notifications"
      aria-current={currentTab === "notifications" ? "page" : undefined}
      aria-description={unread ? "Unread notifications" : undefined}
      className={docked ? "navigation-link navigation-notifications" : "relative"}
      title={collapsed ? "Notifications" : undefined}
    >
      <Bell aria-hidden="true" />
      {docked && <span className="navigation-label">Notifications</span>}
      {unread && <span className="navigation-unread" />}
    </Button>
  );

  const addButton = (
    <Button
      type="button"
      variant="secondary"
      onClick={docked ? undefined : toggleAdd}
      aria-expanded={flow.isMenuOpen}
      aria-label="Add to Stasht"
      title={collapsed ? "Add to Stasht" : undefined}
      className="navigation-action text-ui-card-title"
    >
      <Plus aria-hidden="true" />
      <span className="navigation-label">Add</span>
    </Button>
  );

  const actions = (
    <div className="navigation-actions">
      {!docked && flow.isMenuOpen && (
        <AddContentFlowMenu
          layout="row"
          onSelect={key => {
            close();
            void flow.selectMenuItem(key);
          }}
        />
      )}
      <div className="navigation-action-buttons">
        {docked ? (
          <DropdownMenu
            open={flow.isMenuOpen}
            onOpenChange={open => {
              if (!open) flow.closeMenu();
              else if (!flow.isMenuOpen) toggleAdd();
            }}
          >
            <DropdownMenuTrigger asChild>{addButton}</DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="start">
              <AddContentFlowMenu
                layout="menu"
                onSelect={key => {
                  void flow.selectMenuItem(key);
                }}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          addButton
        )}
        <Button
          type="button"
          variant="secondary"
          onClick={() => openChat({ newChat: true })}
          aria-label="New chat"
          title={collapsed ? "New chat" : undefined}
          className="navigation-action text-ui-card-title"
        >
          <SquarePen aria-hidden="true" />
          <span className="navigation-label">New Chat</span>
        </Button>
      </div>
    </div>
  );

  return (
    <nav aria-label="Menu" className="navigation-menu flex h-full flex-col bg-background">
      {/* The bar's geometry, level with the page's first row beside it. */}
      <div className="navigation-brand">
        <Link
          to={HOME_INFO.to}
          onClick={choose(HOME_INFO)}
          aria-label="Stasht Home"
          title={collapsed ? "Home" : undefined}
          className="rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-4"
        >
          <h2 ref={headingRef} tabIndex={-1} className="text-foreground outline-none">
            <img src="/stasht-icon-circle.svg" alt="" /> <span className="navigation-label">Stasht</span>
          </h2>
        </Link>
        {!docked && notifications}
      </div>

      <div className="navigation-scroll">
        <ul id="main-navigation-pages" className="navigation-links">
          {layout.menu.map(page => {
            const info = page === HOME_PAGE ? HOME_INFO : PAGE_INFO[page];
            return info ? pageRow(info) : null;
          })}
        </ul>
        {docked && actions}
        {docked && notifications}
        <RecentChats onOpen={openChat} collapsed={collapsed} />
      </div>

      {!docked && actions}

      {docked && (
        <div className="navigation-utilities">
          <Button asChild variant="ghost" size="control" className="navigation-link justify-start">
            <Link
              to={HOME_LAYOUT()}
              aria-label="Home and menu"
              title={collapsed ? "Home and menu" : undefined}
            >
              <SlidersHorizontal aria-hidden="true" />
              <span className="navigation-label">Home and menu</span>
            </Link>
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="control"
            className="navigation-link justify-start"
            onClick={onToggleCollapsed}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : undefined}
            aria-expanded={!collapsed}
            aria-controls="main-navigation-pages"
          >
            {collapsed ? <PanelLeftOpen aria-hidden="true" /> : <PanelLeftClose aria-hidden="true" />}
            <span className="navigation-label">Collapse sidebar</span>
          </Button>
        </div>
      )}
      <div className="navigation-footer flex items-center gap-2.5 border-t border-border px-5 pt-3">
        <Link
          to={PROFILE()}
          onClick={event => {
            handleTabClick("profile", event);
            close();
          }}
          aria-label="Profile & settings"
          aria-current={currentTab === "profile" ? "page" : undefined}
          className="navigation-profile flex min-w-0 flex-1 items-center gap-3 rounded-[10px] py-1 transition-opacity hover:opacity-80"
          title={collapsed ? "Profile & settings" : undefined}
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-foreground text-background">
            {profile?.avatar_url ? (
              <img src={profile.avatar_url} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className={ROW_TITLE}>{name.charAt(0).toUpperCase()}</span>
            )}
          </span>
          <span className="navigation-label min-w-0">
            <span className="block truncate text-ui-card-title font-semibold text-foreground">{name}</span>
            <span className={cn(CAPTION, "block")}>Profile &amp; settings</span>
          </span>
        </Link>
        <Button
          type="button"
          variant="secondary"
          size="icon"
          onClick={() => {
            analytics.capture("mobile_search_tapped", { location: "menu" });
            handleTabClick("search");
            close();
          }}
          aria-label="Search saves"
          title={collapsed ? "Search saves" : undefined}
        >
          <Search aria-hidden="true" />
        </Button>
      </div>
    </nav>
  );
}

/** The five chats touched most recently, then all of them in Chat's history (native "Recent chats"). */
function RecentChats({ onOpen, collapsed }: { onOpen: (request: ChatRequest) => void; collapsed: boolean }) {
  const { user } = useAuth();
  const menu = useMenu();
  // The docked menu and phone drawer share the existing thread cache.
  const threads = useChatThreads(user?.user_id, !!menu && (menu.open || menu.docked));
  const recent = useMemo(
    () => [...threads].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, 5),
    [threads],
  );

  return (
    <section aria-labelledby="menu-recent-chats" className="navigation-recent">
      <div className="navigation-recent-heading flex items-center justify-between gap-2 pl-3">
        <h3 id="menu-recent-chats" className={cn(LABEL, "navigation-label text-muted-foreground")}>
          Recent chats
        </h3>
        <Button
          type="button"
          variant={collapsed ? "ghost" : "link"}
          size="control"
          onClick={() => onOpen({ history: true })}
          aria-label="View all chats"
          title={collapsed ? "View all chats" : undefined}
        >
          {collapsed ? <MessageSquare aria-hidden="true" /> : "View all"}
        </Button>
      </div>
      {!collapsed &&
        (recent.length === 0 ? (
          <p className="px-3 pb-2 text-ui-control text-muted-foreground">No recent chats yet</p>
        ) : (
          <ul>
            {recent.map(thread => (
              <li key={thread.id}>
                <button
                  type="button"
                  onClick={() => onOpen({ thread: thread.id })}
                  className={cn(CONTROL, "flex h-(--control-height) w-full items-center rounded-[10px] px-3 text-left text-foreground transition-colors hover:bg-muted")}
                >
                  <span className="min-w-0 flex-1 truncate">{thread.title}</span>
                </button>
              </li>
            ))}
          </ul>
        ))}
    </section>
  );
}
